import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { hasPermission, type Permission } from '@/lib/permissions'
import { AuthContext, useAuth } from '@/hooks/use-auth'
import { resetAllStores } from '@/lib/store-utils'
import { validateAccessTime } from '@/lib/time-utils'
import type { AuthContextType, AuthState, UserProfile, UserRole } from '@/types/auth'

interface AuthProviderProps {
  children: ReactNode
  onAuthStateChange?: (event: string, isAuthenticated: boolean) => void
}

const authLog = (message: string, ...args: unknown[]) => {
  if (import.meta.env.DEV) {
    console.log(`[Auth ${new Date().toISOString()}] ${message}`, ...args)
  }
}

// Tipo para o resultado da query com join
interface UserWithPermissao {
  id: string
  nome: string | null
  email: string | null
  created_at: string
  permissao_id: number | null
  permissao: { id: number; permissao: string; acessos: string[] | null; horario: { inicio: string; fim: string } | null } | null
}

// Fetch de perfil com busca robusta e logs detalhados
async function fetchUserProfile(userId: string, userEmail?: string): Promise<UserProfile | null> {
  console.log('[Auth] fetchUserProfile called with userId:', userId, 'email:', userEmail)

  try {
    // Busca o usuário por ID com join na tabela permissao
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('*, permissao:permissao_id(id, permissao, acessos, horario)')
      .eq('id', userId)
      .maybeSingle()

    console.log('[Auth] Query result - userData:', userData, 'error:', userError)

    if (userError) {
      console.error('[Auth] Error fetching user by ID:', userError.message)
      return null
    }

    if (!userData) {
      console.warn('[Auth] User not found by ID:', userId)
      return null
    }

    // Cast para o tipo correto
    const user = userData as UserWithPermissao
    console.log('[Auth] User found:', {
      id: user.id,
      nome: user.nome,
      email: user.email,
      permissao_id: user.permissao_id,
      permissao: user.permissao,
    })

    // Extrai dados de permissão
    const permissaoData = user.permissao
    console.log('[Auth] Permissao data:', permissaoData)

    // Role vem diretamente do banco (campo permissao.permissao)
    // Se não houver permissão, usa 'sem_permissao' como fallback
    const role: UserRole = permissaoData?.permissao ?? 'sem_permissao'

    // Acessos vêm diretamente do banco (campo permissao.acessos)
    // Se não houver acessos definidos, permite apenas home e perfil
    const acessos: string[] =
      permissaoData?.acessos && Array.isArray(permissaoData.acessos) && permissaoData.acessos.length > 0
        ? permissaoData.acessos
        : ['home', 'perfil']

    console.log('[Auth] Role from database:', role)
    console.log('[Auth] Acessos from database:', acessos)

    const profile: UserProfile = {
      id: user.id,
      nome: user.nome,
      email: user.email,
      created_at: user.created_at,
      permissao_id: user.permissao_id,
      permissao: permissaoData ? {
        id: permissaoData.id,
        created_at: '',
        permissao: permissaoData.permissao,
        acessos: permissaoData.acessos ?? [],
        horario: permissaoData.horario ?? null
      } : null,
      role,
      acessos,
    }

    console.log('[Auth] Final profile:', {
      nome: profile.nome,
      role: profile.role,
      acessos: profile.acessos,
      permissao_id: profile.permissao_id,
    })

    return profile
  } catch (error) {
    console.error('[Auth] Exception in fetchUserProfile:', error)
    return null
  }
}

export function AuthProvider({ children, onAuthStateChange }: AuthProviderProps) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    isLoading: true,
    isProfileLoading: false,
    isAuthenticated: false,
  })
  
  // Refs para controle de promise de inicialização
  const authReadyRef = useRef<Promise<boolean> | null>(null)
  const authReadyResolveRef = useRef<((value: boolean) => void) | null>(null)
  
  // CORREÇÃO: Flag para evitar race conditions - indica se estamos no meio de uma operação manual
  const isManualAuthOperationRef = useRef(false)

  // CORREÇÃO: Flag para indicar se já foi inicializado
  const isInitializedRef = useRef(false)

  // CORREÇÃO: Ref para armazenar acessos atuais - evita stale closure no hasMenuAccess
  // Quando beforeLoad chama hasMenuAccess, o callback pode ter closure com acessos antigos
  // Usando ref, sempre temos acesso ao valor mais recente
  const acessosRef = useRef<string[]>([])
  const hasProfileRef = useRef(false)

  const ensureAuthReadyPromise = useCallback(() => {
    if (!authReadyRef.current) {
      authReadyRef.current = new Promise((resolve) => {
        authReadyResolveRef.current = resolve
      })
    }
    return authReadyRef.current
  }, [])

  const resolveAuthReady = useCallback((isAuthenticated: boolean) => {
    if (authReadyResolveRef.current) {
      authLog('Resolving auth ready promise with:', isAuthenticated)
      authReadyResolveRef.current(isAuthenticated)
      authReadyResolveRef.current = null
    }
  }, [])


  // CORREÇÃO: notifyAuthChange usa ref para evitar dependência no useEffect
  const onAuthStateChangeRef = useRef(onAuthStateChange)
  onAuthStateChangeRef.current = onAuthStateChange
  
  const notifyAuthChange = useCallback(
    (event: string, isAuthenticated: boolean) => {
      onAuthStateChangeRef.current?.(event, isAuthenticated)
    },
    []
  )

  const setAuthenticated = useCallback((user: AuthState['user'], profile: UserProfile | null) => {
    // fetchUserProfile agora sempre retorna um perfil, mas mantemos a verificação por segurança
    if (!profile) {
      authLog('Profile is null - this should not happen with current implementation')
      // CORREÇÃO: Atualiza refs de forma síncrona ANTES do setState
      acessosRef.current = []
      hasProfileRef.current = false
      setState({
        user: null,
        profile: null,
        isLoading: false,
        isProfileLoading: false,
        isAuthenticated: false,
      })
      return
    }

    authLog('Setting authenticated state for:', profile.nome, 'with role:', profile.role)
    // CORREÇÃO: Atualiza refs de forma síncrona ANTES do setState
    // Isso garante que hasMenuAccess tenha os valores corretos imediatamente
    acessosRef.current = profile.acessos ?? []
    hasProfileRef.current = true
    authLog('Refs updated synchronously - acessos:', acessosRef.current)

    setState({
      user,
      profile,
      isLoading: false,
      isProfileLoading: false,
      isAuthenticated: true,
    })
  }, [])

  const setUnauthenticated = useCallback(() => {
    // CORREÇÃO: Atualiza refs de forma síncrona ANTES do setState
    acessosRef.current = []
    hasProfileRef.current = false
    setState({
      user: null,
      profile: null,
      isLoading: false,
      isProfileLoading: false,
      isAuthenticated: false,
    })
  }, [])
  
  // CORREÇÃO: Função centralizada para limpar estado no logout
  const clearAuthState = useCallback(() => {
    authLog('Clearing all auth state, cache and stores')
    queryClient.clear()
    resetAllStores()
    setUnauthenticated()
  }, [queryClient, setUnauthenticated])

  // FIX MULTI-TAB: useEffect baseado em INITIAL_SESSION do onAuthStateChange
  // Em vez de chamar getSession() manualmente (que competia com navigator.locks entre abas),
  // agora usamos apenas onAuthStateChange que dispara INITIAL_SESSION após a library
  // adquirir o lock e resolver a sessão — eliminando a race condition entre abas.
  useEffect(() => {
    let isMounted = true
    void ensureAuthReadyPromise()

    // Safety net de emergência (30s) — só dispara se algo catastrófico acontecer
    // (deadlock no navigator.locks, falha de rede durante init, etc.)
    const failSafeTimer = setTimeout(() => {
      if (!isMounted || isInitializedRef.current) return
      authLog('CRITICAL: Auth initialization timed out after 30s - forcing unauthenticated')
      setUnauthenticated()
      isInitializedRef.current = true
      resolveAuthReady(false)
      notifyAuthChange('TIMEOUT', false)
    }, 30_000)

    // Helper: carrega perfil e resolve auth state.
    // DEVE ser chamado fora do callback do onAuthStateChange (via setTimeout)
    // porque os callbacks rodam dentro do navigator.locks do Supabase,
    // e supabase.from().select() internamente chama getSession() que também
    // precisa do lock → deadlock se chamado diretamente no callback.
    const loadProfileAndResolve = async (
      user: AuthState['user'],
      email: string | undefined,
      eventName: string,
    ) => {
      if (!isMounted || !user) return
      authLog(`${eventName}: loading profile for:`, user.id)
      const profile = await fetchUserProfile(user.id, email)
      if (!isMounted) return

      if (profile) {
        authLog(`${eventName}: profile loaded:`, profile.nome, 'role:', profile.role)
        setAuthenticated(user, profile)
        isInitializedRef.current = true
        resolveAuthReady(true)
        notifyAuthChange(eventName === 'INITIAL_SESSION' ? 'INITIALIZED' : eventName, true)
      } else {
        authLog(`${eventName}: profile not found`)
        setUnauthenticated()
        isInitializedRef.current = true
        resolveAuthReady(false)
        notifyAuthChange(eventName === 'INITIAL_SESSION' ? 'INITIALIZED' : eventName, false)
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      authLog('Auth event:', event, 'isManualOp:', isManualAuthOperationRef.current)
      if (!isMounted) return

      // Ignora eventos durante operação manual (signIn/signOut) para evitar dupla atualização
      if (isManualAuthOperationRef.current) {
        authLog('Ignoring auth event during manual operation')
        return
      }

      if (event === 'INITIAL_SESSION') {
        // Dispara após a library adquirir o lock e resolver a sessão.
        clearTimeout(failSafeTimer)

        if (session?.user) {
          // setTimeout(0) agenda o fetch como macrotask FORA do navigator.locks,
          // evitando deadlock (callback roda dentro do lock, queries precisam do lock)
          const user = session.user as AuthState['user']
          setTimeout(() => void loadProfileAndResolve(user, session.user.email ?? undefined, 'INITIAL_SESSION'), 0)
        } else {
          authLog('INITIAL_SESSION: no session')
          setUnauthenticated()
          isInitializedRef.current = true
          resolveAuthReady(false)
          notifyAuthChange('INITIALIZED', false)
        }
      } else if (event === 'SIGNED_IN' && session?.user) {
        // Sign-ins subsequentes (ex: broadcast de outra aba)
        const user = session.user as AuthState['user']
        setTimeout(() => void loadProfileAndResolve(user, session.user.email ?? undefined, 'SIGNED_IN'), 0)
      } else if (event === 'SIGNED_OUT') {
        // Limpa cache e stores (logout local ou expiração de sessão)
        queryClient.clear()
        resetAllStores()
        setUnauthenticated()
        resolveAuthReady(false)
        notifyAuthChange('SIGNED_OUT', false)
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Atualiza perfil silenciosamente no refresh de token
        const userId = session.user.id
        const userEmail = session.user.email ?? undefined
        const user = session.user as AuthState['user']
        setTimeout(() => {
          void (async () => {
            if (!isMounted) return
            const profile = await fetchUserProfile(userId, userEmail)
            if (!isMounted || !profile) return
            setState(prev => ({ ...prev, user, profile }))
          })()
        }, 0)
      }
    })

    return () => {
      isMounted = false
      clearTimeout(failSafeTimer)
      subscription.unsubscribe()
    }
  }, [ensureAuthReadyPromise, notifyAuthChange, queryClient, resolveAuthReady, setAuthenticated, setUnauthenticated])

  const signIn = useCallback(async (email: string, password: string): Promise<{ error: Error | null; success: boolean }> => {
    authLog('signIn called for:', email)
    
    // CORREÇÃO: Marca que estamos em operação manual para evitar race condition com o listener
    isManualAuthOperationRef.current = true
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })

      if (error) {
        authLog('signIn error:', error.message)
        return { error: new Error(error.message), success: false }
      }

      // Carrega o perfil imediatamente após login bem-sucedido
      if (data.user) {
        console.log('[Auth] signIn: loading profile for user:', data.user.id, data.user.email)
        const profile = await fetchUserProfile(data.user.id, data.user.email ?? undefined)

        if (!profile) {
          console.error('[Auth] signIn: profile not found for authenticated user')
          // Faz logout pois o usuário não tem perfil válido
          await supabase.auth.signOut({ scope: 'local' })
          setUnauthenticated()
          return {
            error: new Error('Perfil de usuário não encontrado. Verifique se seu usuário está cadastrado na tabela users.'),
            success: false
          }
        }

        // Valida janela de horário de acesso
        if (profile.permissao?.horario) {
          const timeValidation = validateAccessTime(profile.permissao.horario)
          if (!timeValidation.isAllowed) {
            console.log('[Auth] signIn: access denied outside allowed hours')
            await supabase.auth.signOut({ scope: 'local' })
            setUnauthenticated()
            return {
              error: new Error(timeValidation.message || 'Acesso nao permitido neste horario'),
              success: false,
            }
          }
        }

        console.log('[Auth] signIn: profile loaded successfully:', profile.nome, 'role:', profile.role, 'acessos:', profile.acessos)
        setAuthenticated(data.user, profile)
        resolveAuthReady(true)
        notifyAuthChange('SIGNED_IN', true)
      }

      return { error: null, success: true }
    } finally {
      // CORREÇÃO: Libera a flag após um pequeno delay para garantir que eventos pendentes sejam ignorados
      setTimeout(() => {
        isManualAuthOperationRef.current = false
      }, 100)
    }
  }, [notifyAuthChange, resolveAuthReady, setAuthenticated, setUnauthenticated])

  // SignOut: clears Supabase session FIRST, then local state
  const signOut = useCallback(async () => {
    authLog('signOut called')

    // Marca operação manual para evitar race condition
    isManualAuthOperationRef.current = true

    try {
      // CORREÇÃO: Limpa a sessão Supabase PRIMEIRO para garantir que o token
      // seja removido do storage antes de qualquer redirecionamento
      await supabase.auth.signOut({ scope: 'local' })
      authLog('signOut: Supabase session cleared')
    } catch (error) {
      authLog('signOut error (ignored):', error)
    }

    // Remove manualmente o storage key como fallback de segurança
    try {
      localStorage.removeItem('painel-barbara-auth')
    } catch {
      // ignore
    }

    // Limpa estado local DEPOIS que a sessão Supabase foi removida
    clearAuthState()

    // Marca como inicializado e não-autenticado para que whenReady()
    // retorne Promise.resolve(false) imediatamente
    authReadyRef.current = null
    authReadyResolveRef.current = null
    isInitializedRef.current = true

    // Notifica mudança de auth
    notifyAuthChange('SIGNED_OUT', false)
    authLog('signOut complete')

    // Libera flag após pequeno delay
    setTimeout(() => {
      isManualAuthOperationRef.current = false
    }, 100)
  }, [clearAuthState, notifyAuthChange])

  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    })
    return { error: error ? new Error(error.message) : null }
  }, [])

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    return { error: error ? new Error(error.message) : null }
  }, [])

  const isAuthorized = useCallback(
    (permission: string): boolean => {
      if (!state.profile) return false
      return hasPermission(state.profile.role, permission as Permission)
    },
    [state.profile]
  )

  // Extrai acessos para usar no contexto
  const acessos = state.profile?.acessos ?? []

  // hasMenuAccess: verifica se o usuário pode acessar um menu específico
  // 100% dinâmico - usa apenas os acessos definidos na tabela permissao
  // CORREÇÃO: Usa refs em vez de estado para evitar stale closures no contexto do router
  const hasMenuAccess = useCallback(
    (menuKey: string): boolean => {
      // Home, perfil e fotos são sempre acessíveis para usuários autenticados
      if (menuKey === 'home' || menuKey === 'perfil' || menuKey === 'fotos') return true

      // CORREÇÃO: Usa refs para ter valores sempre frescos
      const currentAcessos = acessosRef.current
      const currentHasProfile = hasProfileRef.current

      // Se não tem perfil, nega acesso (exceto home/perfil)
      if (!currentHasProfile) {
        console.log('[Auth] hasMenuAccess: no profile, denying access to', menuKey)
        return false
      }

      // Acesso total: 'all' no array de acessos (definido no banco)
      if (currentAcessos.includes('all')) {
        console.log('[Auth] hasMenuAccess: user has "all" access, granting', menuKey)
        return true
      }

      // Verifica acesso específico no array de acessos do banco
      const hasAccess = currentAcessos.includes(menuKey)
      console.log('[Auth] hasMenuAccess:', menuKey, '=', hasAccess, 'acessos:', currentAcessos)
      return hasAccess
    },
    [] // CORREÇÃO: Sem dependências - usa refs que sempre têm valor fresco
  )

  // whenReady: retorna promise que resolve quando a autenticação está pronta
  // Usado pelos route guards (beforeLoad) para esperar o auth resolver antes de decidir
  const whenReady = useCallback((): Promise<boolean> => {
    if (!state.isLoading && isInitializedRef.current) {
      authLog('whenReady: returning current state:', state.isAuthenticated)
      return Promise.resolve(state.isAuthenticated)
    }

    const promise = ensureAuthReadyPromise()
    authLog('whenReady: waiting for auth ready promise (isLoading:', state.isLoading, 'initialized:', isInitializedRef.current, ')')
    return promise
  }, [ensureAuthReadyPromise, state.isAuthenticated, state.isLoading])

  const value: AuthContextType = {
    ...state,
    signIn,
    signOut,
    signUp,
    resetPassword,
    isAuthorized,
    hasMenuAccess,
    role: state.profile?.role ?? null,
    acessos, // Já extraído acima com fallback para []
    whenReady,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// useAuth (hook) exportado junto do AuthProvider. Afeta apenas o fast-refresh em dev,
// não a produção; manter juntos preserva a API de import atual do projeto.
// eslint-disable-next-line react-refresh/only-export-components
export { useAuth }
